import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmtDate, fmtDateTime, StatusBadge } from "@/utils/format";
import { toast } from "sonner";
import { ArrowLeft, BadgePercent, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";

const TABS = ["Bilgiler", "Ekstre", "Siparişler"];
const FIYAT_LISTELERI = ["Perakende", "Esnaf", "Servis"];
const EK_ISKONTO_HARIC_MARKALAR = new Set(["VW ORİJİNAL", "PORSCHE ORİJİNAL"]);

export default function CariDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cari, setCari] = useState(null);
  const [tab, setTab] = useState(0);
  const [ekstre, setEkstre] = useState([]);
  const [ekstreTotal, setEkstreTotal] = useState(0);
  const [ekstrePage, setEkstrePage] = useState(1);
  const [siparisler, setSiparisler] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get(`/cariler/${id}`).then((r) => {
      setCari(r.data);
      setForm({ ...r.data, ek_iskonto: r.data.ek_iskonto || 0, marka_iskontolari: r.data.marka_iskontolari || [] });
    }).catch(() => navigate("/cariler"));
  }, [id, navigate]);

  useEffect(() => {
    api.get("/stoklar/brands").then((r) => setBrands(
      (r.data.brands || []).filter((brand) => !EK_ISKONTO_HARIC_MARKALAR.has(String(brand).trim().toUpperCase()))
    )).catch(() => setBrands([]));
  }, []);

  const loadEkstre = useCallback(async () => {
    const r = await api.get(`/cariler/ekstre/${id}`, { params: { page: ekstrePage, per_page: 30 } });
    setEkstre(r.data.items);
    setEkstreTotal(r.data.total);
  }, [id, ekstrePage]);

  const loadSiparisler = useCallback(async () => {
    const r = await api.get("/siparisler/", { params: { cari_id: id, per_page: 20 } });
    setSiparisler(r.data.items);
  }, [id]);

  useEffect(() => { if (tab === 1) loadEkstre(); }, [tab, loadEkstre]);
  useEffect(() => { if (tab === 2) loadSiparisler(); }, [tab, loadSiparisler]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put(`/cariler/${id}`, form);
      setCari(r.data);
      toast.success("Cari güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateBrandDiscount = (index, field, value) => {
    setForm((previous) => {
      const rows = [...(previous.marka_iskontolari || [])];
      if (field === "marka" && rows.some((row, rowIndex) => rowIndex !== index && row.marka === value)) {
        toast.error("Bu marka için zaten ek iskonto tanımlı");
        return previous;
      }
      rows[index] = { ...rows[index], [field]: value };
      return { ...previous, marka_iskontolari: rows };
    });
  };

  const addBrandDiscount = () => {
    const used = new Set((form.marka_iskontolari || []).map((row) => row.marka));
    const firstAvailable = brands.find((brand) => !used.has(brand));
    if (!firstAvailable) return toast.error("Eklenebilecek başka marka bulunamadı");
    setForm((previous) => ({
      ...previous,
      marka_iskontolari: [...(previous.marka_iskontolari || []), { marka: firstAvailable, oran: 0 }],
    }));
  };

  if (!cari) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-[#0D1B3E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const F = ({ label, name, type = "text", readOnly }) => (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
      <input
        type={type}
        value={form[name] ?? ""}
        onChange={(e) => setForm((p) => ({ ...p, [name]: type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))}
        readOnly={readOnly}
        className={`w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors ${readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`}
      />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/cariler")} className="text-slate-400 hover:text-[#0D1B3E] transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>{cari.unvan}</h1>
          <p className="text-sm text-slate-500">Cari Kodu: <span className="font-mono">{cari.kod}</span></p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className={`text-lg font-bold tabular-nums ${(cari.bakiye ?? 0) > 0 ? "text-red-600" : "text-green-600"}`}>
            {fmtCurrency(cari.bakiye ?? 0)}
          </div>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{cari.fiyat_listesi || "Perakende"}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === i ? "border-[#0D1B3E] text-[#0D1B3E]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Bilgiler */}
      {tab === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <F label="Cari Kodu" name="kod" readOnly />
              <F label="Ünvan" name="unvan" />
              <F label="Vergi No" name="vergi_no" />
              <F label="Vergi Dairesi" name="vergi_dairesi" />
              <F label="Telefon" name="telefon" />
              <F label="E-Posta" name="email" type="email" />
              <F label="İl" name="il" />
              <F label="İlçe" name="ilce" />
              <F label="Grup" name="grup" />
              <F label="Vade Gün" name="vade_gun" type="number" />
              <F label="Bakiye" name="bakiye" type="number" />
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Fiyat Listesi</label>
                <select
                  value={form.fiyat_listesi || "Perakende"}
                  onChange={(e) => setForm((p) => ({ ...p, fiyat_listesi: e.target.value }))}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20"
                >
                  {FIYAT_LISTELERI.map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <BadgePercent size={18} className="mt-0.5 text-[#0D1B3E]" />
                  <div>
                    <h2 className="text-sm font-bold text-[#0D1B3E]">Müşteriye özel artı iskonto</h2>
                    <p className="text-xs text-slate-500 mt-0.5">Aftermarket ürünlerde fiyat listesi ve kampanya iskontosuna ardışık uygulanır.</p>
                  </div>
                </div>
                <div className="w-full sm:w-44">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Genel ek iskonto %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.ek_iskonto ?? 0}
                    onChange={(e) => setForm((p) => ({ ...p, ek_iskonto: Math.min(100, Math.max(0, Number(e.target.value) || 0)) }))}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20"
                  />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700">Markaya özel artı iskonto</h3>
                    <p className="text-xs text-slate-500">Genel ek iskonto ile birlikte uygulanır.</p>
                  </div>
                  <button type="button" onClick={addBrandDiscount} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-[#0D1B3E] hover:bg-slate-50">
                    <Plus size={14} /> Marka ekle
                  </button>
                </div>
                {(form.marka_iskontolari || []).length === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-4 text-center text-xs text-slate-400">Markaya özel iskonto tanımlanmadı.</div>
                ) : (form.marka_iskontolari || []).map((row, index) => (
                  <div key={`${row.marka}-${index}`} className="grid grid-cols-[minmax(0,1fr)_120px_38px] gap-2 items-end">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Marka</label>
                      <select value={row.marka} onChange={(e) => updateBrandDiscount(index, "marka", e.target.value)} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                        {brands.map((brand) => <option key={brand} value={brand}>{brand}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Ek iskonto %</label>
                      <input type="number" min="0" max="100" step="0.01" value={row.oran ?? 0} onChange={(e) => updateBrandDiscount(index, "oran", Math.min(100, Math.max(0, Number(e.target.value) || 0)))} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" />
                    </div>
                    <button type="button" aria-label="Marka iskontosunu kaldır" onClick={() => setForm((p) => ({ ...p, marka_iskontolari: p.marka_iskontolari.filter((_, rowIndex) => rowIndex !== index) }))} className="h-[38px] w-[38px] inline-flex items-center justify-center rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">VW ORİJİNAL ve PORSCHE ORİJİNAL ürünlere müşteri ek iskontoları uygulanmaz.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Adres</label>
              <textarea
                value={form.adres || ""}
                onChange={(e) => setForm((p) => ({ ...p, adres: e.target.value }))}
                rows={2}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20"
              />
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="px-5 py-2 text-sm rounded-md bg-[#0D1B3E] text-white hover:bg-[#172A58] disabled:opacity-60 transition-colors">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Ekstre */}
      {tab === 1 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="table-container">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr>
                  {["Tarih", "Açıklama", "Belge No", "Borç", "Alacak", "Kaynak"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ekstre.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Hareket bulunamadı</td></tr>
                ) : ekstre.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 tabular-nums text-xs text-slate-500">{fmtDate(h.tarih)}</td>
                    <td className="px-4 py-3 text-slate-700">{h.aciklama}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{h.belge_no || "-"}</td>
                    <td className="px-4 py-3 tabular-nums font-medium text-red-600">{h.borc ? fmtCurrency(h.borc) : "-"}</td>
                    <td className="px-4 py-3 tabular-nums font-medium text-green-600">{h.alacak ? fmtCurrency(h.alacak) : "-"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{h.kaynak || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <span className="text-xs text-slate-500">{ekstreTotal} kayıt</span>
            <div className="flex items-center gap-1">
              <button disabled={ekstrePage <= 1} onClick={() => setEkstrePage((p) => p - 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40">
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs text-slate-600 px-2">Sayfa {ekstrePage}</span>
              <button disabled={ekstre.length < 30} onClick={() => setEkstrePage((p) => p + 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40">
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Siparişler */}
      {tab === 2 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Sipariş No", "Tarih", "Durum", "Toplam"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {siparisler.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">Sipariş bulunamadı</td></tr>
              ) : siparisler.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-sm font-medium">{s.siparis_no}</td>
                  <td className="px-4 py-3 tabular-nums text-xs text-slate-500">{fmtDate(s.tarih)}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.durum} /></td>
                  <td className="px-4 py-3 tabular-nums font-semibold">{fmtCurrency(s.toplam_tutar)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
